import { useEffect, useRef } from 'react';

/** Long enough to collapse a burst of typing, short enough to survive a fast tab close. */
export const DRAFT_SAVE_DEBOUNCE_MS = 400;

/**
 * Write a draft after the user stops typing, not on every keystroke.
 *
 * The naive version serialises the whole draft — including a 30-question `parsedQuestions`
 * array — and calls `localStorage.setItem` synchronously on each character. That is a
 * main-thread write per keystroke in the biggest forms in the app.
 *
 * The trailing edge is flushed on unmount, so navigating away mid-burst still persists
 * what was typed: dropping the last few characters would be a small version of exactly
 * the data loss this whole spec is about.
 */
export function useDebouncedDraftSave(save: () => void, deps: unknown[]): void {
  // Kept in a ref so the timer always calls the CURRENT closure without restarting on
  // every render. Assigned in an effect, not during render (react-hooks/refs).
  const latest = useRef(save);
  useEffect(() => {
    latest.current = save;
  });

  useEffect(() => {
    const timer = setTimeout(() => latest.current(), DRAFT_SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  // Flush on unmount so a navigation mid-burst does not lose the tail of the typing.
  useEffect(() => () => latest.current(), []);
}
